/**
 * Chemical Table Component
 * Chemical-specific inventory table showing unit configuration and dual quantities (drums + total measure)
 */

import React, { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
  Collapse, Chip, IconButton, Stack, alpha, useTheme, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress
} from '@mui/material';
import {
  ExpandMore, ExpandLess, Edit as EditIcon,
  Description as DescriptionIcon, Receipt as ReceiptIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon, Science as ScienceIcon,
  LocalShipping as LocalShippingIcon, Warning as WarningIcon,
  CheckCircle as CheckCircleIcon, Search as SearchIcon,
  Delete as DeleteIcon, Close as CloseIcon, Error as ErrorIcon
} from '@mui/icons-material';
import EditLotDialog from '../EditLotDialog';
import EditReorderDialog from '../EditReorderDialog';
import PaginationController from '../PaginationController';
import { supabase } from '../../../../../supabaseClient';

const LOT_BATCH_DEFAULT_ROWS = 12;

// Returns a capitalized, singular/plural unit label based on quantity
function unitLabel(qty, rawUnitType) {
  if (!rawUnitType) return '';
  const lower = rawUnitType.toLowerCase();
  const singularForm = lower.endsWith('s') ? lower.slice(0, -1) : lower;
  const pluralForm   = lower.endsWith('s') ? lower : lower + 's';
  const base = qty === 1 ? singularForm : pluralForm;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

// Capitalize and singularize (for "per X" contexts)
function singularLabel(rawUnitType) {
  if (!rawUnitType) return '';
  const lower = rawUnitType.toLowerCase();
  const irregulars = { boxes: 'Box', bottles: 'Bottle', bundles: 'Bundle', cases: 'Case', drums: 'Drum', gallons: 'Gallon', kilograms: 'Kilogram', liters: 'Liter', pails: 'Pail', pallets: 'Pallet', pieces: 'Piece', rolls: 'Roll', tubes: 'Tube', units: 'Unit' };
  if (irregulars[lower]) return irregulars[lower];
  const singularForm = lower.endsWith('es') ? lower.slice(0, -2) : lower.endsWith('s') ? lower.slice(0, -1) : lower;
  return singularForm.charAt(0).toUpperCase() + singularForm.slice(1);
}

function cleanNotes(notes) {
  if (!notes) return '';
  const segments = notes.split(';').map(s => s.trim()).filter(Boolean);
  return segments.filter(seg => {
    if (seg.match(/^Adjusted to \d+/)) return false;
    if (seg.startsWith('Transfer merge - Added')) return false;
    if (seg.startsWith('Transfer from location - Ref:')) return false;
    if (seg.startsWith('Adjustment created lot/batch - Ref:')) return false;
    if (seg.match(/^Merged transfer from /)) return false;
    return true;
  }).join('; ');
}

function getStatusSeverity(item) {
  const qty = item.total_quantity || 0;
  if (qty === 0) return 'error';
  const rp = item.reorder_point || 0;
  if (rp > 0 && qty <= rp) return 'warning';
  return 'success';
}

export default function ChemicalTable({ data = [], config, inventoryType, expandable = false, onRefresh, noContainerBorder = false, canEdit = true, canDelete = true, primaryLocationId = null }) {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState({});
  const [lotBatchSearchTerms, setLotBatchSearchTerms] = useState({});
  const [lotBatchPagination, setLotBatchPagination] = useState({});
  const [editLotDialogOpen, setEditLotDialogOpen] = useState(false);
  const [editReorderDialogOpen, setEditReorderDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteLotDialogOpen, setDeleteLotDialogOpen] = useState(false);
  const [deleteAggregateDialogOpen, setDeleteAggregateDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleRowClick = (key) => {
    if (!expandable) return;
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const confirmDeleteLot = async () => {
    if (!itemToDelete) return;
    try {
      setDeleting(true);

      // Purge inactive records with same lot/batch to prevent reuse
      await supabase
        .from(config.itemsTable)
        .delete()
        .eq('company_id', itemToDelete.company_id)
        .eq(config.itemIdField, itemToDelete[config.itemIdField])
        .eq('location_id', itemToDelete.location_id)
        .eq('lot_number', itemToDelete.lot_number)
        .eq('batch_number', itemToDelete.batch_number)
        .eq('is_active', false);

      let sourceLotNumber = itemToDelete.lot_number || null;
      let sourceBatchNumber = itemToDelete.batch_number || null;
      if (itemToDelete.source_transaction_id) {
        const { data: srcTx } = await supabase
          .from(config.transactionsTable)
          .select('lot_number, batch_number')
          .eq('id', itemToDelete.source_transaction_id)
          .single();
        if (srcTx) {
          sourceLotNumber = srcTx.lot_number || sourceLotNumber;
          sourceBatchNumber = srcTx.batch_number || sourceBatchNumber;
        }
        await supabase.from(config.transactionsTable).delete().eq('id', itemToDelete.source_transaction_id);
      }

      const deletedQty = Math.abs(parseFloat(itemToDelete.current_unit_quantity || itemToDelete.current_quantity) || 0);
      const deletedTotalQty = Math.abs(parseFloat(itemToDelete.current_total_quantity) || deletedQty);
      const itemName = itemToDelete[config.tableName]?.name || itemToDelete.item_name || 'item';
      const lotParts = [
        sourceLotNumber ? `Lot: ${sourceLotNumber}` : null,
        sourceBatchNumber ? `Batch: ${sourceBatchNumber}` : null
      ].filter(Boolean).join(', ');

      await supabase.from(config.transactionsTable).insert({
        company_id: itemToDelete.company_id,
        [config.itemIdField]: itemToDelete[config.itemIdField],
        transaction_type: 'inventory_deletion',
        unit_quantity: -deletedQty,
        total_quantity: -deletedTotalQty,
        location_id: itemToDelete.location_id,
        lot_number: sourceLotNumber,
        batch_number: sourceBatchNumber,
        transaction_date: new Date().toISOString(),
        notes: `Lot deleted — ${itemName}${lotParts ? ` (${lotParts})` : ''}`
      });

      // Delete batch_ingredient_lots first to satisfy FK constraint
      await supabase.from('batch_ingredient_lots').delete().eq('inventory_item_id', itemToDelete.id);

      const { error } = await supabase.from(config.itemsTable).delete().eq('id', itemToDelete.id);
      if (error) throw error;

      // Recalculate aggregate via chemical RPC
      await supabase.rpc('recalculate_raw_materials_aggregate_inventory', {
        p_company_id:      itemToDelete.company_id,
        p_raw_material_id: itemToDelete[config.itemIdField],
        p_location_id:     itemToDelete.location_id
      });

      if (onRefresh) await onRefresh();
      setDeleteLotDialogOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting lot:', err);
      alert('Failed to delete lot/batch: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteAggregate = async () => {
    if (!itemToDelete) return;
    try {
      setDeleting(true);

      const actualItemId = itemToDelete.item_id || itemToDelete[config.itemIdField];
      if (!actualItemId) throw new Error('Could not determine item ID');

      const { data: allLotItems } = await supabase
        .from(config.itemsTable)
        .select('id, source_transaction_id, lot_number, batch_number, current_unit_quantity, current_total_quantity')
        .eq('company_id', itemToDelete.company_id)
        .eq(config.itemIdField, actualItemId)
        .eq('location_id', itemToDelete.location_id);

      const lotsWithInfo = [];
      if (allLotItems && allLotItems.length > 0) {
        for (const lotItem of allLotItems) {
          let lotNumber = lotItem.lot_number || null;
          let batchNumber = lotItem.batch_number || null;
          if (lotItem.source_transaction_id) {
            const { data: srcTx } = await supabase
              .from(config.transactionsTable)
              .select('lot_number, batch_number')
              .eq('id', lotItem.source_transaction_id)
              .single();
            if (srcTx) {
              lotNumber = srcTx.lot_number || lotNumber;
              batchNumber = srcTx.batch_number || batchNumber;
            }
          }
          lotsWithInfo.push({
            lot_number: lotNumber,
            batch_number: batchNumber,
            quantity: Math.abs(parseFloat(lotItem.current_unit_quantity) || 0),
            total_quantity: Math.abs(parseFloat(lotItem.current_total_quantity) || 0)
          });
        }
        const txIds = allLotItems.map(i => i.source_transaction_id).filter(Boolean);
        if (txIds.length > 0) {
          await supabase.from(config.transactionsTable).delete().in('id', txIds);
        }
      }

      const itemName = itemToDelete.item_name || 'item';
      const totalDeletedQty = lotsWithInfo.reduce((sum, l) => sum + l.quantity, 0);
      const totalDeletedTotalQty = lotsWithInfo.reduce((sum, l) => sum + l.total_quantity, 0);

      await supabase.from(config.transactionsTable).insert({
        company_id: itemToDelete.company_id,
        [config.itemIdField]: actualItemId,
        transaction_type: 'inventory_deletion',
        unit_quantity: -totalDeletedQty,
        total_quantity: -totalDeletedTotalQty,
        location_id: itemToDelete.location_id,
        transaction_date: new Date().toISOString(),
        notes: `All inventory deleted — ${itemName}`
      });

      // Clear batch_ingredient_lots FK before deleting items
      const itemIds = (allLotItems || []).map(i => i.id).filter(Boolean);
      if (itemIds.length > 0) {
        await supabase.from('batch_ingredient_lots').delete().in('inventory_item_id', itemIds);
      }

      // Delete each lot item by primary key ID
      for (const lotId of itemIds) {
        await supabase.from(config.itemsTable).delete().eq('id', lotId);
      }
      // Also catch any remaining via compound key
      await supabase.from(config.itemsTable).delete()
        .eq('company_id', itemToDelete.company_id)
        .eq(config.itemIdField, actualItemId)
        .eq('location_id', itemToDelete.location_id);

      // Fetch aggregate record ID, then delete by primary key
      const { data: aggRow } = await supabase.from(config.inventoryTable).select('id')
        .eq('company_id', itemToDelete.company_id)
        .eq(config.itemIdField, actualItemId)
        .eq('location_id', itemToDelete.location_id)
        .maybeSingle();
      if (aggRow) {
        await supabase.from(config.inventoryTable).delete().eq('id', aggRow.id);
      }

      if (onRefresh) await onRefresh();
      setDeleteAggregateDialogOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting aggregate:', err);
      alert('Failed to delete inventory: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const viewDocument = async (bucket, filePath) => {
    if (!bucket || !filePath) return;
    try {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(filePath, 3600);
      if (error || !data?.signedUrl) return;
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
    }
  };

  const documentTypeMap = {
    invoice: { label: 'INV',  color: 'primary', bucket: 'raw-materials-transactions-invoices', pathKey: 'invoice_file_path', icon: <ReceiptIcon fontSize="small" /> },
    po:      { label: 'PO',   bucket: 'raw-material-inventory-po',      pathKey: 'po_file_path',   icon: <DescriptionIcon fontSize="small" />, sx: { bgcolor: '#00897b', color: 'white', '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor: '#00695c' } } },
    coa:     { label: 'COA',  color: 'success', bucket: 'raw-materials-inventory-coa',  pathKey: 'coa_file_path',  icon: <AssignmentTurnedInIcon fontSize="small" /> },
    msds:    { label: 'MSDS', color: 'warning', bucket: 'raw-materials-inventory-msds', pathKey: 'msds_file_path', icon: <ScienceIcon fontSize="small" /> },
    bol:     { label: 'BOL',  color: 'info',    bucket: 'raw-material-inventory-bol',  pathKey: 'bol_file_path',  icon: <LocalShippingIcon fontSize="small" /> }
  };

  const renderDocuments = (lotItem) => {
    const trans = lotItem.source_transaction || {};
    const available = Object.entries(documentTypeMap)
      .filter(([, doc]) => trans[doc.pathKey])
      .map(([key, doc]) => ({ ...doc, key, path: trans[doc.pathKey] }));

    if (available.length === 0) {
      return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>No documents</Typography>;
    }
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center" alignItems="center">
        {available.map(doc => (
          <Chip key={doc.key} label={doc.label} size="small" color={doc.sx ? undefined : doc.color} icon={doc.icon} clickable
            onClick={() => viewDocument(doc.bucket, doc.path)}
            sx={{ fontSize: '0.75rem', height: 24, ...(doc.sx || {}) }} />
        ))}
      </Stack>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h6" color="text.secondary">No chemical inventory found</Typography>
      </Paper>
    );
  }

  const hSx = {
    fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    color: 'primary.main', py: 2, px: 3
  };

  return (
    <>
      <TableContainer
        component={noContainerBorder ? 'div' : Paper}
        sx={noContainerBorder ? { overflow: 'visible' } : {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.15)}`,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...hSx, minWidth: 250 }}>Chemical</TableCell>
              <TableCell sx={hSx}>Location</TableCell>
              <TableCell sx={hSx}>Unit Configuration</TableCell>
              <TableCell sx={hSx} align="center">Current Stock</TableCell>
              <TableCell sx={hSx} align="center">Reorder Point</TableCell>
              <TableCell sx={hSx} align="center">Reorder Qty</TableCell>
              <TableCell sx={hSx} align="center">Status</TableCell>
              <TableCell sx={{ ...hSx, width: 120 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...data].sort((a, b) => {
              const nameCmp = (a.item_name || '').localeCompare(b.item_name || '');
              if (nameCmp !== 0) return nameCmp;
              const aP = primaryLocationId && a.location_id === primaryLocationId ? 0 : 1;
              const bP = primaryLocationId && b.location_id === primaryLocationId ? 0 : 1;
              return aP - bP || (a.location_name || '').localeCompare(b.location_name || '');
            }).map((item, index) => {
              const rowKey = `${item.item_id}-${item.location_id}`;
              const isExpanded = expandedRows[rowKey];
              const lotItems = item.lots || [];
              const unitQty = item.total_quantity || 0;
              const totalBaseQty = item.total_base_quantity || 0;
              const unitType = item.unit_type || 'units';
              const unitMeasure = item.unit_measure || '';
              const unitQtyPerUnit = item.unit_qty_per_unit;
              const reorderPoint = item.reorder_point || 0;
              const reorderQty = item.reorder_quantity || 0;
              const status = getStatusSeverity(item);
              const hasInventory = unitQty > 0;

              return (
                <React.Fragment key={`${rowKey}-${index}`}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'default',
                      transition: 'all 0.2s ease',
                      bgcolor: hasInventory ? 'white' : alpha(theme.palette.error.main, 0.02),
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s ease',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 3, px: 3
                      }
                    }}
                  >
                    {/* Chemical Name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {expandable && hasInventory && lotItems.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRowClick(rowKey)}
                            sx={{ color: 'primary.main', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 1.5 }}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', mb: 0.3 }}>
                            {item.item_name}
                          </Typography>
                          {item.item_sku && (
                            <Typography variant="caption" color="text.secondary">SKU: {item.item_sku}</Typography>
                          )}
                          {expandable && hasInventory && lotItems.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {lotItems.length} lot{lotItems.length !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Location */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.primary' }}>
                        {item.location_name}
                      </Typography>
                    </TableCell>

                    {/* Unit Configuration */}
                    <TableCell>
                      <Typography variant="h6" sx={{ fontWeight: 400, color: 'primary.main', fontSize: '1.05rem', mb: 0.5 }}>
                        {unitQtyPerUnit
                          ? `${unitQtyPerUnit}${unitMeasure} per ${singularLabel(unitType)}`
                          : singularLabel(unitType)}
                      </Typography>
                      {unitMeasure && unitType && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          {unitMeasure} / {singularLabel(unitType)}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Current Stock */}
                    <TableCell align="center">
                      <Typography variant="h5" sx={{ fontWeight: 500, color: hasInventory ? 'text.primary' : 'text.disabled', fontSize: '1.3rem', mb: 0.5 }}>
                        {unitQty.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 0.5 }}>
                        {unitLabel(unitQty, unitType)}
                      </Typography>
                      {totalBaseQty > 0 && unitMeasure && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                          {totalBaseQty.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}{unitMeasure} total
                        </Typography>
                      )}
                    </TableCell>

                    {/* Reorder Point */}
                    <TableCell align="center">
                      <Typography variant="h6" sx={{ fontWeight: 400, color: 'primary.main', fontSize: '1.05rem' }}>
                        {reorderPoint > 0
                          ? `${reorderPoint.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} ${unitLabel(reorderPoint, unitType)}`
                          : 'Not set'}
                      </Typography>
                    </TableCell>

                    {/* Reorder Qty */}
                    <TableCell align="center">
                      <Typography variant="h6" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '1.05rem' }}>
                        {reorderQty > 0
                          ? `${reorderQty.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} ${unitLabel(reorderQty, unitType)}`
                          : 'Not set'}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center">
                      {status === 'error'   && <Chip label="Out of Stock" size="small" color="error"   icon={<ErrorIcon />} />}
                      {status === 'warning' && <Chip label="Low Stock"    size="small" color="warning" icon={<WarningIcon />} />}
                      {status === 'success' && <Chip label="In Stock"     size="small" color="success" icon={<CheckCircleIcon />} />}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <IconButton size="small" color="primary" disabled={!canEdit} onClick={() => { setSelectedItem(item); setEditReorderDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" disabled={!canDelete} onClick={() => { setItemToDelete(item); setDeleteAggregateDialogOpen(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expandable Lot Rows */}
                  {expandable && (
                    <TableRow>
                      <TableCell sx={{ py: 0, px: 0, border: 'none' }} colSpan={8}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{
                            p: 4, bgcolor: 'white',
                            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
                                Lot/Batch Details ({lotItems.length} lot{lotItems.length !== 1 ? 's' : ''})
                              </Typography>
                              <TextField
                                placeholder="Search lot/batch..."
                                size="small"
                                value={lotBatchSearchTerms[rowKey] || ''}
                                onChange={(e) => setLotBatchSearchTerms(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                    </InputAdornment>
                                  )
                                }}
                                sx={{
                                  width: { xs: '100%', sm: '400px' },
                                  '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: 'white' }
                                }}
                              />
                            </Box>

                            {(() => {
                              const searchTerm = (lotBatchSearchTerms[rowKey] || '').toLowerCase();
                              const lbPage = lotBatchPagination[rowKey]?.page ?? 0;
                              const lbRowsPerPage = lotBatchPagination[rowKey]?.rowsPerPage ?? LOT_BATCH_DEFAULT_ROWS;
                              const filtered = lotItems.filter(lot => {
                                if (!searchTerm) return true;
                                return (lot.lot_number || '').toLowerCase().includes(searchTerm)
                                  || (lot.batch_number || '').toLowerCase().includes(searchTerm);
                              });
                              const paginated = filtered.slice(lbPage * lbRowsPerPage, (lbPage + 1) * lbRowsPerPage);

                              return (
                                <>
                                  <TableContainer component={Paper} sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                    overflow: 'hidden'
                                  }}>
                                    <Table>
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }}>Quantity</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }}>Lot/Batch Details</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }}>Dates</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }}>Vendor & Invoice</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }} align="center">Status</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }} align="center">Documents</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2.5, px: 3 }} align="center">Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {paginated.map((lotItem, lotIdx) => {
                                          const lotUnitQty = lotItem.current_unit_quantity || lotItem.current_quantity || 0;
                                          const lotTotalQty = lotItem.current_total_quantity || 0;

                                          // Expiry logic (parse in local timezone to avoid off-by-one)
                                          const parseDateLocal = (ds) => {
                                            if (!ds) return null;
                                            const [y, m, d] = ds.split('-').map(Number);
                                            if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
                                            return new Date(y, m - 1, d);
                                          };
                                          const formatDateOnly = (ds) => {
                                            const dt = parseDateLocal(ds);
                                            return dt ? dt.toLocaleDateString() : '—';
                                          };
                                          const today = new Date(); today.setHours(0, 0, 0, 0);
                                          const in30Days = new Date(today); in30Days.setDate(today.getDate() + 30);
                                          const expDateObj = parseDateLocal(lotItem.expiry_date);
                                          const isExpired      = expDateObj && expDateObj <= today;
                                          const isExpiringSoon = expDateObj && !isExpired && expDateObj < in30Days;

                                          return (
                                            <TableRow
                                              key={`lot-${lotItem.id}-${lotIdx}`}
                                              hover
                                              sx={{
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                  bgcolor: 'grey.50',
                                                  transform: 'translateX(4px)',
                                                  transition: 'all 0.2s ease',
                                                  boxShadow: 'inset 4px 0 0 black'
                                                },
                                                '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.grey[50], 0.3) }
                                              }}
                                            >
                                              <TableCell sx={{ py: 3, px: 3 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', mb: 0.5 }}>
                                                  {lotUnitQty.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {unitLabel(lotUnitQty, unitType)}
                                                </Typography>
                                                {lotTotalQty > 0 && unitMeasure && (
                                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                                    {lotTotalQty.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}{unitMeasure} total
                                                  </Typography>
                                                )}
                                              </TableCell>

                                              <TableCell sx={{ py: 3, px: 3 }}>
                                                {lotItem.lot_number && (
                                                  <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>Lot: {lotItem.lot_number}</Typography>
                                                )}
                                                {lotItem.batch_number && (
                                                  <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 0.5 }}>Batch: {lotItem.batch_number}</Typography>
                                                )}
                                                {!lotItem.lot_number && !lotItem.batch_number && (
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>No lot/batch info</Typography>
                                                )}
                                                {lotItem.notes && cleanNotes(lotItem.notes) && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    {cleanNotes(lotItem.notes)}
                                                  </Typography>
                                                )}
                                              </TableCell>

                                              {/* Dates column */}
                                              <TableCell sx={{ py: 3, px: 3 }}>
                                                {lotItem.manufactured_date && (
                                                  <Typography variant="body1" sx={{ fontWeight: 400, display: 'block', fontSize: '0.9rem', mb: 0.5 }}>
                                                    Mfg: {formatDateOnly(lotItem.manufactured_date)}
                                                  </Typography>
                                                )}
                                                {lotItem.expiry_date && (
                                                  <Typography
                                                    variant="body1"
                                                    sx={{
                                                      fontWeight: isExpired || isExpiringSoon ? 500 : 400,
                                                      display: 'block',
                                                      fontSize: '0.9rem',
                                                      color: isExpired ? 'error.main' : isExpiringSoon ? 'warning.main' : 'text.primary'
                                                    }}
                                                  >
                                                    Exp: {formatDateOnly(lotItem.expiry_date)}
                                                    {isExpired && ' (EXPIRED)'}
                                                    {isExpiringSoon && ' (Soon)'}
                                                  </Typography>
                                                )}
                                                {!lotItem.manufactured_date && !lotItem.expiry_date && (
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>No date info</Typography>
                                                )}
                                              </TableCell>

                                              <TableCell sx={{ py: 3, px: 3 }}>
                                                {lotItem.vendor_info?.supplier_name && (
                                                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                    {lotItem.vendor_info.supplier_name}
                                                  </Typography>
                                                )}
                                                {lotItem.vendor_info?.unit_cost && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    Cost: ${parseFloat(lotItem.vendor_info.unit_cost).toFixed(2)}/{singularLabel(unitType)}
                                                  </Typography>
                                                )}
                                                {lotItem.vendor_info?.invoice_number && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    Invoice #: {lotItem.vendor_info.invoice_number}
                                                  </Typography>
                                                )}
                                                {lotItem.vendor_info?.invoice_date && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    Invoice Date: {new Date(lotItem.vendor_info.invoice_date).toLocaleDateString()}
                                                  </Typography>
                                                )}
                                                {lotItem.vendor_info?.po_number && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    PO #: {lotItem.vendor_info.po_number}
                                                  </Typography>
                                                )}
                                                {lotItem.vendor_info?.po_date && (
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    PO Date: {new Date(lotItem.vendor_info.po_date).toLocaleDateString()}
                                                  </Typography>
                                                )}
                                              </TableCell>

                                              {/* Status: active + expiry */}
                                              <TableCell sx={{ py: 3, px: 3 }} align="center">
                                                <Stack spacing={0.5} alignItems="center">
                                                  {!isExpired && !isExpiringSoon && (
                                                    <Chip
                                                      label={lotItem.is_active ? 'Active' : 'Inactive'}
                                                      size="small"
                                                      color={lotItem.is_active ? 'success' : 'default'}
                                                      icon={lotItem.is_active ? <CheckCircleIcon /> : undefined}
                                                    />
                                                  )}
                                                  {isExpired && (
                                                    <Chip label="Expired" size="small" color="error" icon={<ErrorIcon />} />
                                                  )}
                                                  {isExpiringSoon && (
                                                    <Chip label="Expiring Soon" size="small" color="warning" icon={<WarningIcon />} />
                                                  )}
                                                </Stack>
                                              </TableCell>

                                              <TableCell sx={{ py: 3, px: 3 }} align="center">
                                                {renderDocuments(lotItem)}
                                              </TableCell>

                                              <TableCell sx={{ py: 3, px: 3 }} align="center">
                                                <IconButton size="small" color="primary" disabled={!canEdit} onClick={() => { setSelectedItem({ ...lotItem, _unitType: unitType, _unitMeasure: unitMeasure, _unitQtyPerUnit: unitQtyPerUnit }); setEditLotDialogOpen(true); }}>
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" disabled={!canDelete} onClick={() => { setItemToDelete(lotItem); setDeleteLotDialogOpen(true); }}>
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                        {paginated.length === 0 && (
                                          <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                              <Typography color="text.secondary">No lots found</Typography>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                  {filtered.length > LOT_BATCH_DEFAULT_ROWS && (
                                    <PaginationController
                                      count={filtered.length}
                                      page={lbPage}
                                      onPageChange={(_, newPage) => setLotBatchPagination(prev => ({
                                        ...prev,
                                        [rowKey]: { page: newPage, rowsPerPage: prev[rowKey]?.rowsPerPage ?? LOT_BATCH_DEFAULT_ROWS }
                                      }))}
                                      rowsPerPage={lbRowsPerPage}
                                      onRowsPerPageChange={(val) => setLotBatchPagination(prev => ({
                                        ...prev,
                                        [rowKey]: { page: 0, rowsPerPage: val }
                                      }))}
                                      sx={{ py: 1.5, px: 2 }}
                                    />
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedItem && (
        <>
          <EditLotDialog
            open={editLotDialogOpen}
            onClose={() => { setEditLotDialogOpen(false); setSelectedItem(null); }}
            item={selectedItem} config={config} inventoryType={inventoryType} onSuccess={onRefresh} canDelete={canDelete}
          />
          <EditReorderDialog
            open={editReorderDialogOpen}
            onClose={() => { setEditReorderDialogOpen(false); setSelectedItem(null); }}
            item={selectedItem} config={config} inventoryType={inventoryType} onSuccess={onRefresh}
          />
        </>
      )}

      {/* Delete Lot Dialog */}
      <Dialog open={deleteLotDialogOpen} onClose={() => !deleting && setDeleteLotDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pb: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>Delete Lot/Batch</Typography>
            <Typography variant="body2" color="text.secondary">This will remove this lot/batch and update inventory totals.</Typography>
          </Box>
          <IconButton onClick={() => setDeleteLotDialogOpen(false)} disabled={deleting} sx={{ mt: -1, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {itemToDelete && (
            <Paper elevation={0} sx={{ p: 2.5, bgcolor: alpha(theme.palette.error.main, 0.04), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600, mb: 1 }}>
                {itemToDelete.item_name || itemToDelete.name}
              </Typography>
              <Typography variant="body2"><strong>Lot:</strong> {itemToDelete.lot_number || '—'} / Batch: {itemToDelete.batch_number || '—'}</Typography>
              <Typography variant="body2">
                <strong>Quantity:</strong> {(itemToDelete.current_unit_quantity || itemToDelete.current_quantity || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {data[0]?.unit_type || 'units'}
              </Typography>
            </Paper>
          )}
          <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningIcon sx={{ color: 'error.main', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteLotDialogOpen(false)} disabled={deleting} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteLot} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ borderRadius: 2, px: 3 }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Aggregate Dialog */}
      <Dialog open={deleteAggregateDialogOpen} onClose={() => !deleting && setDeleteAggregateDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pb: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex', alignItems: 'center' }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>Delete All Inventory</Typography>
            <Typography variant="body2" color="text.secondary">This will remove ALL lots/batches and inventory records for this chemical at this location.</Typography>
          </Box>
          <IconButton onClick={() => setDeleteAggregateDialogOpen(false)} disabled={deleting} sx={{ mt: -1, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {itemToDelete && (
            <Paper elevation={0} sx={{ p: 2.5, bgcolor: alpha(theme.palette.error.main, 0.04), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600, mb: 1 }}>{itemToDelete.item_name}</Typography>
              <Typography variant="body2"><strong>Location:</strong> {itemToDelete.location_name}</Typography>
              <Typography variant="body2">
                <strong>Total Stock:</strong> {(itemToDelete.total_quantity || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {itemToDelete.unit_type || 'units'}
              </Typography>
            </Paper>
          )}
          <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.08), border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`, borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarningIcon sx={{ color: 'error.main', mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteAggregateDialogOpen(false)} disabled={deleting} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteAggregate} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ borderRadius: 2, px: 3 }}>
            {deleting ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
